import { createSelector } from 'reselect';

import { RequestState, StateDictionary, StateStatus } from '../reducers/utils/state-manager';

export type ContactFileState = {
  contactFilesById: { [contactFileId: number]: ContactFile };
  contactFilesStateByContactId: StateDictionary;
  presignedUrlsById: {
    [contactFileId: number]: { presignedUrl: string; expiresAt: string };
  };
  presignedUrlsStateByContactFileIds: { [contactFileIds: string]: RequestState };
};
type State = { contactFiles: ContactFileState };

const contactFilesByIdSelector = (state: State) => state.contactFiles.contactFilesById;
const contactFilesSelector = createSelector([contactFilesByIdSelector], (contactFilesById) =>
  Object.values(contactFilesById),
);

export const contactFilesForContactIdSelector = createSelector(
  [contactFilesSelector, (_state: State, contactId: number) => contactId],
  (contactFiles, contactId) => contactFiles.filter((file) => file.contactId === contactId),
);

export const contactFilesForEmployeeIdSelector = createSelector(
  [contactFilesSelector, (_state: State, employeeId: number) => employeeId],
  (contactFiles, employeeId) => contactFiles.filter((file) => file.employeeId === employeeId),
);

export const contactFilesForApplicantIdSelector = createSelector(
  [contactFilesSelector, (_state: State, jobApplicantId: number | string) => jobApplicantId],
  (contactFiles, jobApplicantId) =>
    contactFiles.filter((file) => file.jobApplicantId === jobApplicantId),
);

const contactFilesStateByContactIdSelector = (state: State) =>
  state.contactFiles.contactFilesStateByContactId;
export const contactFilesStateForContactIdSelector = createSelector(
  [contactFilesStateByContactIdSelector, (_state: State, contactId: number) => contactId],
  (contactFilesStateByContactId, contactId) => contactFilesStateByContactId[contactId],
);

const presignedUrlsByIdSelector = (state: State) => state.contactFiles.presignedUrlsById;
export const contactFilesWithPresignedUrlsForIdsSelector = createSelector(
  [
    presignedUrlsByIdSelector,
    contactFilesByIdSelector,
    (_state: State, contactFileIds: number[]) => contactFileIds,
  ],
  (presignedUrlsByContactFileId, contactFilesById, contactFileIds) =>
    contactFileIds.map((contactFileId) => ({
      contactFile: contactFilesById[contactFileId] as ContactFile | undefined,
      presignedUrl: presignedUrlsByContactFileId[contactFileId]?.presignedUrl,
      expiresAt: presignedUrlsByContactFileId[contactFileId]?.presignedUrl,
    })),
);

const presignedUrlsStateByContactFileIdsSelector = (state: State) =>
  state.contactFiles.presignedUrlsStateByContactFileIds;
export const contactFilesWithPresignedUrlsStateForIdsSelector = createSelector(
  [
    presignedUrlsByIdSelector,
    presignedUrlsStateByContactFileIdsSelector,
    (_state: any, contactFileIds: number[]) => contactFileIds,
  ],
  (presignedUrlsById, presignedUrlsState, contactFileIds) => {
    if (contactFileIds.length === 0) {
      return StateStatus.success;
    }

    const requestState = presignedUrlsState[JSON.stringify(contactFileIds)];

    // if any successfully loaded URLs are expired, invalidate the cache
    if (
      requestState === StateStatus.success &&
      contactFileIds.find((contactFileId) => {
        const { expiresAt } = presignedUrlsById[contactFileId] || {};
        return expiresAt && new Date() > new Date(expiresAt);
      })
    ) {
      return StateStatus.dirty;
    }

    // if no state present, indicate that the request is unattempted
    return requestState || StateStatus.notAttempted;
  },
);
